/**
 * Lo común de los widgets de la ficha (contactos, productos): el período y el top
 * elegidos en el menú, los meses del eje, «Otros», y cómo se escriben montos, meses y
 * fechas. Cada widget define `ops` (con `date`), `t()` y su `data`.
 */
import { CHART_TYPES } from "@/components/gotrading/dashboard/widgetData";
import { formatNumberSync } from "@/utils/numberFormatter";

export const MONTH_OPTIONS = [6, 12, 24, 0];
export const LIMIT_OPTIONS = [5, 8, 12, 20];
const pad2 = (n) => String(n).padStart(2, "0");

export default {
  computed: {
    locale() {
      return (this.$i18n && this.$i18n.locale) || "es";
    },
    chartType() {
      const t = this.config.chart || this.entry.chart || "column";
      return CHART_TYPES.includes(t) ? t : "column";
    },
    months() {
      const n = this.config.months;
      return MONTH_OPTIONS.includes(n) ? n : 12;
    },
    limit() {
      const n = this.config.limit;
      return LIMIT_OPTIONS.includes(n) ? n : 8;
    },
    /** Meses del eje: los últimos N (con los vacíos en cero), o desde la primera operación. */
    monthKeys() {
      const now = new Date();
      let count = this.months;
      if (!count) {
        const first = this.ops.map((o) => o.date).filter(Boolean).sort()[0];
        if (!first) return [];
        const [y, m] = first.split("-").map((x) => parseInt(x, 10));
        count = (now.getUTCFullYear() - y) * 12 + (now.getUTCMonth() + 1 - m) + 1;
        count = Math.min(Math.max(count, 1), 120);
      }
      const keys = [];
      for (let i = count - 1; i >= 0; i -= 1) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
        keys.push(`${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`);
      }
      return keys;
    },
  },
  methods: {
    perMonth(rows) {
      const keys = this.monthKeys;
      const map = Object.fromEntries(keys.map((k) => [k, 0]));
      rows.forEach((r) => {
        if (r.month in map) map[r.month] += r.value;
      });
      if (!rows.length || keys.every((k) => !map[k])) return [];
      return keys.map((k) => ({ label: this.monthLabel(k), value: Math.round(map[k] * 100) / 100 }));
    },
    /** Los N más grandes; el resto, en «Otros». */
    top(rows) {
      const sorted = rows.filter((r) => r.value).sort((a, b) => b.value - a.value);
      // Desde 100 sin decimales: el formato del tenant puede traer 4.
      const round = (v) => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100);
      if (sorted.length <= this.limit) return sorted.map((r) => ({ label: r.label, value: round(r.value) }));
      const head = sorted.slice(0, this.limit - 1).map((r) => ({ label: r.label, value: round(r.value) }));
      const rest = sorted.slice(this.limit - 1).reduce((s, r) => s + r.value, 0);
      return head.concat({ label: this.t("others"), value: round(rest), other: true });
    },
    /** Algunos nombres importados traen entidades HTML (`&#350;`). */
    contactName(id) {
      const name = (this.data && this.data.contacts && this.data.contacts[id]) || "—";
      return name.replace(/&#(\d+);/g, (m, code) => String.fromCharCode(parseInt(code, 10))).replace(/&amp;/g, "&");
    },
    cur(id) {
      if (!id) return this.$t("dashboardWidgets.finance.noCurrency");
      return (this.data && this.data.currencies && this.data.currencies[id]) || "";
    },
    money(v) {
      const n = Number(v) || 0;
      const digits = Math.abs(n) >= 1000 ? 0 : 2;
      try {
        return new Intl.NumberFormat(this.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
      } catch (e) {
        return formatNumberSync(n) || "0";
      }
    },
    monthLabel(key) {
      const [y, m] = String(key).split("-").map((x) => parseInt(x, 10));
      const d = new Date(Date.UTC(y, m - 1, 1));
      return new Intl.DateTimeFormat(this.locale, { month: "short", year: "2-digit", timeZone: "UTC" }).format(d).replace(".", "");
    },
    dateText(ymd) {
      if (!ymd) return "—";
      const [y, m, d] = ymd.split("-");
      return `${d}/${m}/${y}`;
    },
    shortDate(ymd) {
      if (!ymd) return "";
      const [y, m, d] = ymd.split("-");
      return `${d}/${m}/${y.slice(2)}`;
    },
  },
};
